import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../../utils/authStorage';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import { generateYearRange } from '../../../../../Custom/Custom';
import asiTitle from '../../../../../asi.json';
import BlockJJsonData from '../../../../../assets/BlockJData.json';

// API configuration
const getAuthConfig = () => {
  if (!hasAuthSession()) return null;
  return { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Cache-Control': 'no-cache' } };
};

const BlockJReport = () => {
  const navigate = useNavigate();

  // State for report data
  const [itemData, setItemData] = useState({});
  const [blockJData, setBlockJData] = useState([]);
  const [blockgdata, setblockgdata] = useState([]);
  const [loading, setLoading] = useState(true);

  // Memoized block info from localStorage
  const blockInfo = useMemo(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    if (!balanceSheetData) return {};

    const parsedData = JSON.parse(balanceSheetData);
    return {
      dslNo: parsedData.dslNo || '',
      surveyYear: parsedData.year || '2022-23',
      nameUnit: parsedData.nameUnit || '',
      pslNo: parsedData.pslNo || ''
    };
  }, []);

  const storedLanguage = localStorage.getItem('language') || 'en';
  const itemCodeArray = ['9921100', '9995000', '9999900'];

  // Memoized data transformation
  const transformBlockJData = useCallback((data) => {
    const initialData = {};
    data.forEach((item, index) => {
      initialData[index] = {
        id: index,
        dslNo: blockInfo.dslNo,
        surveyYear: blockInfo.surveyYear,
        itemDesc: item.itemDesc,
        itemCode: item.itemCode,
        unitOfQty: item.unitOfQty,
        qtyManufactured: item.qtyManufactured,
        qtySold: item.qtySold,
        grossSaleValue: item.grossSaleValue,
        perunitNetsaleValue: item.perunitNetsaleValue,
        exfactoryValueQty: item.exfactoryValueQty,
        statusFlag: item.statusFlag,
        distributiveExpensesExciseDuty: item.distributiveExpensesExciseDuty,
        distributiveExpensesSalesTaxVat: item.distributiveExpensesSalesTaxVat,
        distributiveExpensesOthers: item.distributiveExpensesOthers,
        distributiveExpensesTotal: item.distributiveExpensesTotal,
        itemCodeClass: 'textBox_back_color_white'
      };
    });
    return initialData;
  }, [blockInfo.dslNo, blockInfo.surveyYear]);

  // Function to add empty rows
  const addNineEmptyRow = useCallback((responseBlockJData, dataType, emptyRowCount) => {
    let customObject = [];
    let mergedArray = '';
    for (let i = 0; i <= emptyRowCount; i++) {
      const newItemAdded = {
        "dslNo": blockInfo.dslNo,
        "surveyYear": blockInfo.surveyYear,
        "itemDesc": "",
        "itemCode": "",
        "unitOfQty": "",
        "qtyManufactured": "",
        "qtySold": "",
        "grossSaleValue": "",
        "perunitNetsaleValue": "",
        "exfactoryValueQty": "",
        "statusFlag": "",
        "distributiveExpensesExciseDuty": "",
        "distributiveExpensesSalesTaxVat": "",
        "distributiveExpensesOthers": "",
        "distributiveExpensesTotal": ""
      };
      customObject.push(newItemAdded);
    }

    if (dataType === "dynamic") {
      const dataArray = Object.values(responseBlockJData);
      const AddedIndex = dataArray.length - 3
      mergedArray = [
        ...responseBlockJData.slice(0, AddedIndex),
        ...customObject,
        ...responseBlockJData.slice(AddedIndex)
      ];
    } else if (dataType === "static") {
      mergedArray = [
        ...responseBlockJData.slice(0, 0),
        ...customObject,
        ...responseBlockJData.slice(0)
      ];
    }

    return mergedArray;
  }, [blockInfo.dslNo, blockInfo.surveyYear]);

  // Fetch data function
  const fetchBlockJData = useCallback(async () => {
    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year');
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0]
    };

    showLoading(true);

    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return;
      }

      const response = await axios.post(`${API_URL}api/asi/blockJForm`, requestPayload, config);
      const { data, status } = response.data;

      let responseBlockJData = '';

      if (status && data?.blockJData?.length > 0) {
        let dy = 0;
        if (data.blockJData.length > 3) {
          dy = -1;
        }
        responseBlockJData = addNineEmptyRow(data.blockJData, 'dynamic', dy);
        setblockgdata(data.paramG?.valuesG || []);
      } else {
        responseBlockJData = addNineEmptyRow(BlockJJsonData, 'static', 0);
      }

      setBlockJData(responseBlockJData);
      const transformedData = transformBlockJData(responseBlockJData);
      setItemData(transformedData);
      console.log('Block J data loaded successfully:', responseBlockJData.length, 'items');
    } catch (error) {
      console.error('API Error:', error);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      } else {
        toast.error(`Error loading data: ${error.message}`, messages.COLOR_RED);
      }
      setBlockJData([]);
    } finally {
      showLoading(false);
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear, transformBlockJData, addNineEmptyRow]);

  useEffect(() => {
    fetchBlockJData();
  }, [fetchBlockJData]);

  // Memoized table headers
  const tableHeaders = useMemo(() => {
    const isHindi = storedLanguage === 'hi';
    return {
      slNo: isHindi ? asiTitle.hi.SL_NO : asiTitle.en.SL_NO,
      productDescription: isHindi ? asiTitle.hi.PRODUCT_DESCRIPTION : asiTitle.en.PRODUCT_DESCRIPTION,
      itemCode: isHindi ? asiTitle.hi.ITEM_CODE : asiTitle.en.ITEM_CODE,
      unitOfQty: isHindi ? asiTitle.hi.UNIT_OF_QTY : asiTitle.en.UNIT_OF_QTY,
      quantityManufactured: isHindi ? asiTitle.hi.QUANTITY_MANUFACTURED : asiTitle.en.QUANTITY_MANUFACTURED,
      qtySold: isHindi ? asiTitle.hi.QTY_SOLD : asiTitle.en.QTY_SOLD,
      grossSale: isHindi ? asiTitle.hi.GROSS_SALE : asiTitle.en.GROSS_SALE,
      distributiveExpenses: isHindi ? asiTitle.hi.DISTRIBUTIVE_EXPENSES : asiTitle.en.DISTRIBUTIVE_EXPENSES,
      gst: isHindi ? asiTitle.hi.GST : asiTitle.en.GST,
      exerciseDuty: isHindi ? asiTitle.hi.EXERCISE_DUTY : asiTitle.en.EXERCISE_DUTY,
      otherDistributiveExpenses: isHindi ? asiTitle.hi.OTHER_DISTRIBUTIVE_EXPENSES : asiTitle.en.OTHER_DISTRIBUTIVE_EXPENSES,
      subsidy: isHindi ? asiTitle.hi.SUBSIDY : asiTitle.en.SUBSIDY,
      perUnitSale: isHindi ? asiTitle.hi.PER_UNIT_SALE : asiTitle.en.PER_UNIT_SALE,
      exFactoryValue: isHindi ? asiTitle.hi.EX_FACTORY_VALUE : asiTitle.en.EX_FACTORY_VALUE,
      dslNo: isHindi ? asiTitle.hi.DSL_NO : asiTitle.en.DSL_NO,
      surveyYear: isHindi ? asiTitle.hi.SERVEY_YEAR : asiTitle.en.SERVEY_YEAR,
      blockJTitle: isHindi ? asiTitle.hi.BLOCK_J_TITLE : asiTitle.en.BLOCK_J_TITLE
    };
  }, [storedLanguage]);

  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      <style>
        {`
          .report-container {
            background-color: #fff;
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 20px;
            margin-bottom: 20px;
          }
          .report-title {
            background-color: #fff;
            color: #333;
            font-size: 18px;
            font-weight: bold;
            padding: 15px 20px;
            margin: -20px -20px 20px -20px;
            border-radius: 6px 6px 0 0;
            border-bottom: 2px solid #333;
            text-align: center;
          }
          .report-info {
            background-color: #f8f9fa;
            padding: 10px 15px;
            margin-bottom: 20px;
            border-radius: 4px;
            border-left: 4px solid #333;
          }
          .report-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 10px;
          }
          .report-table th,
          .report-table td {
            border: 1px solid #333;
            padding: 3px 2px;
            text-align: center;
            vertical-align: middle;
          }
          .report-table th {
            background-color: #f5f5f5;
            color: #333;
            font-weight: bold;
            font-size: 9px;
          }
          .report-table td {
            background-color: #fff;
          }
          .report-table .item-name {
            text-align: left;
            font-weight: 500;
            padding-left: 4px;
          }
          .report-table .bold-text {
            font-weight: bold;
          }
          .no-data {
            text-align: center;
            color: #666;
            font-style: italic;
            padding: 40px;
            background-color: #f8f9fa;
            border-radius: 4px;
          }
          .btn_orange {
            background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
            border: none;
            color: white;
            padding: 8px 16px;
            border-radius: 6px;
            font-weight: 500;
            transition: all 0.3s ease;
            box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
          }
          .btn_orange:hover {
            background: linear-gradient(135deg, #e55a2b 0%, #e6851a 100%);
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(255, 107, 53, 0.4);
            color: white;
          }
          @media print {
            .no-print { display: none !important; }
            .report-container { border: none; margin: 0; padding: 0; }
            .report-title { margin: 0; border-radius: 0; }
            .report-info { margin: 0 0 20px 0; }
            .btn_orange { display: none !important; }
          }
        `}
      </style>

      <div className="container-fluid py-3">
        {/* Report Content */}
        <div className="report-container">
          <div className="report-title">
            <div dangerouslySetInnerHTML={{ __html: tableHeaders.blockJTitle }} />
          </div>

          <div className="report-info">
            <div className="row">
              <div className="col-md-6">
                <strong>{tableHeaders.dslNo}:</strong> {blockInfo.dslNo || 'Not Available'}
              </div>
              <div className="col-md-6">
                <strong>{tableHeaders.surveyYear}:</strong> {blockInfo.surveyYear || 'Not Available'}
              </div>
            </div>
          </div>

          {blockJData.length > 0 ? (
            <div className="table-responsive">
              <table className="report-table">
                <thead>
                  <tr>
                    <th className="text-center" rowSpan="2" style={{ width: '5%' }}>{tableHeaders.slNo}</th>
                    <th className="text-center" rowSpan="2" style={{ width: '15%' }}>{tableHeaders.productDescription}</th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}>{tableHeaders.itemCode}</th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}>{tableHeaders.unitOfQty}</th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}><div dangerouslySetInnerHTML={{ __html: tableHeaders.quantityManufactured }} /></th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}>{tableHeaders.qtySold}</th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}><div dangerouslySetInnerHTML={{ __html: tableHeaders.grossSale }} /></th>
                    <th className="text-center" colSpan="4" style={{ width: '32%' }}>{tableHeaders.distributiveExpenses}</th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}><div dangerouslySetInnerHTML={{ __html: tableHeaders.perUnitSale }} /></th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}><div dangerouslySetInnerHTML={{ __html: tableHeaders.exFactoryValue }} /></th>
                  </tr>
                  <tr>
                    <th className="text-center" style={{ width: '8%' }}>{tableHeaders.gst}</th>
                    <th className="text-center" style={{ width: '8%' }}>{tableHeaders.exerciseDuty}</th>
                    <th className="text-center" style={{ width: '8%' }}>{tableHeaders.otherDistributiveExpenses}</th>
                    <th className="text-center" style={{ width: '8%' }}>{tableHeaders.subsidy}</th>
                  </tr>
                  <tr>
                    <th className="text-center">1</th>
                    <th className="text-center">2</th>
                    <th className="text-center">3</th>
                    <th className="text-center">4</th>
                    <th className="text-center">5</th>
                    <th className="text-center">6</th>
                    <th className="text-center">7</th>
                    <th className="text-center">8</th>
                    <th className="text-center">9</th>
                    <th className="text-center">10</th>
                    <th className="text-center">11</th>
                    <th className="text-center">12</th>
                    <th className="text-center">13</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(itemData).map((item, i) => {
                    let srNo = i + 1; // Start from 1, not 0
                    let staticNo = 10;
                    let isIndex = false;
                    let dataLength = Object.values(itemData).length;

                    if (i >= dataLength - 3) {
                      isIndex = true;
                    }

                    if (itemCodeArray.includes(item.itemCode) && i >= dataLength - 3) {
                      // This is a total row
                    }

                    const isBold = ['9995000'].includes(item.itemCode);

                    return (
                      <tr key={i}>
                        <td>{isIndex ? ++staticNo : srNo}</td>
                        <td className={`item-name ${isBold ? "bold-text" : ""}`}>
                          {item.itemDesc}
                        </td>
                        <td>{item.itemCode}</td>
                        <td>{item.unitOfQty}</td>
                        <td>{item.qtyManufactured === "0" ? "" : item.qtyManufactured || ''}</td>
                        <td>{item.qtySold === "0" ? "" : item.qtySold || ''}</td>
                        <td>{item.grossSaleValue === "0" ? "" : item.grossSaleValue || ''}</td>
                        <td>{item.distributiveExpensesExciseDuty === "0" ? "" : item.distributiveExpensesExciseDuty || ''}</td>
                        <td>{item.distributiveExpensesSalesTaxVat === "0" ? "" : item.distributiveExpensesSalesTaxVat || ''}</td>
                        <td>{item.distributiveExpensesOthers === "0" ? "" : item.distributiveExpensesOthers || ''}</td>
                        <td>{item.distributiveExpensesTotal === "0" ? "" : item.distributiveExpensesTotal || ''}</td>
                        <td>{item.perunitNetsaleValue === "0" ? "" : item.perunitNetsaleValue || ''}</td>
                        <td>{item.exfactoryValueQty === "0" ? "" : item.exfactoryValueQty || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">No Block J data available for this DSL No.</div>
          )}
        </div>

        {/* Action Buttons */}
        {/* <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print  
              <i className="fa fa-print ms-1"></i>
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/reports')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div> */}

        <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/report-selection')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/blockKAndLreport')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlockJReport;